import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Collapse, FormCheck } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { CellContext, createColumnHelper } from "@tanstack/react-table";

import TanstackTable from "@/components/TanstackTable";
import { FaultLogApi } from "@/services/FaultLog.api";
import { FaultLog } from "@/shared/models/FaultLog";
import { DateTimeHelper } from "@/shared/utils/DateTime.helper";
import { ExcelHelper } from "@/shared/utils/Excel.helper";
import FaultLogFilter from "./FaultLogFilter";
import { RootState, store } from "@/redux/store";
import AdvancedFilter from "@/components/AdvancedSearch";
import { AdvancedSearchFilterNameItem } from "@/shared/models/advanced-search/AdvancedSearchFilterNameItem";
import { Constants, Pages } from "@/shared/Constants";
import { IDashboardState, resetFilters, setAdvancedFilters, setOpenAdvancedQuery, setOpenFilter } from "@/redux/dashboardSlice";
import {
  DataType,
  FaultLogCustomerFlagList,
  FaultLogLogisticsImpactList,
  FaultLogOwnerList,
  FaultLogPriorityList,
  FaultLogStatusList,
} from "@/shared/Enums";
import MessageData from "@/components/MessageData";
import "./style.css";
import {
  dateRangeFilterFn,
  arrayTextFilterFn,
  selectFilterFn,
  arraySelectFilterFn,
  arrayNumberRangeFilterFn,
} from "@/components/TanstackTable/Filter";
import { useAuth } from "@/context/AuthContext";
import MultilineText from "@/components/MultilineText";
import CardContainer from "@/components/CardContainer";
import { UserProfileApi } from "@/services/UserProfile.api";
import { isHandler, isReader } from "@/shared/utils/roles";
import { useCommonData } from "@/hooks/useCommonData";
import { selectAuth } from "@/redux/authSlice";
import { BusinessHelper } from "@/shared/utils/Business.helper";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardState = useSelector((state: RootState) => state.dashboard);
  const toastInfo = useSelector((state: RootState) => state.share.toastInfo);
  const { countryRegionDomains, countryOptions, products, faultCodesCodeList, regionOptions } = useCommonData();
  const { apexId: currentApexId } = useSelector((state: RootState) => state.auth);
  const { userRoles, userDomain } = useAuth();
  const { isClientProfileSet } = useSelector(selectAuth);

  useEffect(() => {
    if (!isClientProfileSet) {
      navigate(`/${Pages.clientProfile}`, { replace: true });
    }
  }, [isClientProfileSet, navigate]);

  const [data, setData] = useState([] as FaultLog[]);

  const columnHelper = createColumnHelper<FaultLog>();

  const loading = useCallback(
    async (stateOverride?: IDashboardState) => {
      const faultLogData = await FaultLogApi.query(stateOverride || dashboardState, countryRegionDomains, userDomain, userRoles);
      if (!stateOverride?.openAdvancedQuery) {
        const [euHandlerSpontaneouslyRecoveredFlashingItems, handlerClosedNotifiedFlashingItems] = await Promise.all([
          BusinessHelper.LoadEUHandlerSpontaneouslyRecoveredFlashingItems(
            dashboardState,
            countryRegionDomains,
            userDomain,
            userRoles
          ),
          BusinessHelper.LoadHandlerClosedNotifiedFlashingItems(
            dashboardState,
            countryRegionDomains,
            userDomain,
            userRoles
          ),
        ]);

        // The two override queries overlap on EU spontaneous recoveries, so deduplicate them before merging
        const overrideItems = [
          ...euHandlerSpontaneouslyRecoveredFlashingItems,
          ...handlerClosedNotifiedFlashingItems.filter(
            (item) => !euHandlerSpontaneouslyRecoveredFlashingItems.some((faultLog) => faultLog.id === item.id)
          ),
        ];

        // Combine and deduplicate by id. Keep the main query item when duplicates exist.
        const combinedData = [
          ...overrideItems,
          ...(faultLogData || []).filter((item) => !overrideItems.some((faultLog) => faultLog.id === item.id)),
        ];
        setData(combinedData);
      } else {
        setData(faultLogData);
      }
    },
    [dashboardState, countryRegionDomains, userRoles, userDomain]
  );

  const handleAcknowledgedChange = useCallback(
    async (faultLog: FaultLog, acknowledged: boolean) => {
      if (!acknowledged) {
        return;
      }

      const message = acknowledged
        ? `Are you sure you want to acknowledge Fault Log ID ${faultLog.id}?`
        : `Are you sure you want to unacknowledge Fault Log ID ${faultLog.id}?`;

      if (window.confirm(message)) {
        await FaultLogApi.ack(faultLog.id);
        await loading();
      }
    },
    [loading]
  );

  const showEditForm = useCallback(
    (data: FaultLog) => {
      navigate(`/${Pages.faultLogDetail}?id=${data.id}`, {
        state: { from: Pages.dashboard },
        replace: false,
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: Constants.Label_ID,
        cell: (info: CellContext<FaultLog, number>) => (
          <span
            onClick={() => {
              showEditForm(info.row.original);
            }}
            className="ap-color"
            style={{ cursor: "pointer", textDecoration: "underline", userSelect: "text" }}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("acknowledged", {
        header: Constants.Label_Acknowledged,
        cell: (info) => {
          var row = info.row.original;
          return (
            <FormCheck
              disabled={!row.needAck}
              onClick={() => handleAcknowledgedChange(row, !info.getValue())}
              checked={info.getValue()}
              readOnly
            />
          );
        },
      }),
      columnHelper.accessor("status", {
        header: Constants.Label_Status,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("priority", {
        header: Constants.Label_Priority,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("faultCode", {
        header: Constants.Label_FaultCode,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("reportedTime", {
        header: Constants.Label_ReportedTime,
        cell: (info) => DateTimeHelper.ToLocalString(info.getValue()!),
        filterFn: dateRangeFilterFn,
      }),
      columnHelper.accessor("customerShipTo", {
        header: Constants.Label_CustomerShipTo,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("customerDemandType", {
        header: Constants.Label_CustomerDemandType,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("customerName", {
        header: Constants.Label_CustomerName,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("region", {
        header: Constants.Label_Region,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("owner", {
        header: Constants.Label_Owner,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("impactOnLogistics", {
        header: Constants.Label_ImpactOnLogistics,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("vesselId", {
        header: Constants.Label_VesselID,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("workOrderNumber", { header: Constants.Label_WorkOrderNumber, cell: (info) => info.getValue() }),
      columnHelper.accessor("workOrderUserStatus", {
        header: Constants.Label_WorkOrderUserStatus,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("latestComments", {
        header: Constants.Label_LatestComments,
        cell: (info) => {
          const value = info.getValue();
          return value ? <div style={{ maxWidth: "300px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value}</div> : null;
        },
      }),
      columnHelper.accessor("product", {
        header: Constants.Label_Product,
        cell: (info) => <MultilineText text={info.getValue()} />,
        meta: { filterVariant: "arraySelect" },
        filterFn: arraySelectFilterFn,
      }),
      columnHelper.accessor("deviceId", {
        header: Constants.Label_DeviceID,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("deviceType", {
        header: Constants.Label_DeviceType,
        cell: (info) => <MultilineText text={info.getValue()} />,
        filterFn: arrayTextFilterFn,
      }),
      columnHelper.accessor("telemetrySystem", {
        header: Constants.Label_TelemetrySystem,
        cell: (info) => info.getValue(),
        meta: { filterVariant: "select" },
        filterFn: selectFilterFn,
      }),
      columnHelper.accessor("acknowledgedBy", { header: Constants.Label_AcknowledgedBy, cell: (info) => info.getValue() }),
      columnHelper.accessor("acknowledgedTime", {
        header: Constants.Label_AcknowledgedTime,
        cell: (info) => DateTimeHelper.ToLocalString(info.getValue()!),
        filterFn: dateRangeFilterFn,
      }),
      columnHelper.accessor("numOfDeliveries", {
        header: Constants.Label_NumOfDeliveriesInLast30Days,
        cell: (info) => <MultilineText text={info.getValue()} />,
        meta: { filterVariant: "arrayNumberRange" },
        filterFn: arrayNumberRangeFilterFn,
      }),
    ],
    [columnHelper, handleAcknowledgedChange, showEditForm]
  );

  const AdvancedFitlerfieldNameList: AdvancedSearchFilterNameItem[] = useMemo(
    () => [
      {
        label: Constants.Label_Acknowledged,
        name: Constants.Field_Acknowledged,
        type: DataType.Boolean,
      },
      {
        label: Constants.Label_AcknowledgedBy,
        name: Constants.Field_AcknowledgedBy,
        type: DataType.Text,
      },
      {
        label: Constants.Label_AssetName,
        name: Constants.Field_AssetName,
        type: DataType.Text,
      },
      {
        label: Constants.Label_ClosedDate,
        name: Constants.Field_ClosedDate,
        type: DataType.Date,
      },
      {
        label: Constants.Label_Country,
        name: Constants.Field_Country,
        type: DataType.DropDown,
        dropDownList: countryOptions.map((cd) => cd.value),
      },
      {
        label: Constants.Label_CustomerCity,
        name: Constants.Field_CustomerCity,
        type: DataType.Text,
      },
      {
        label: Constants.Label_CustomerDemandType,
        name: Constants.Field_CustomerDemandType,
        type: DataType.Text,
      },
      {
        label: Constants.Label_CustomerFlag,
        name: Constants.Field_CustomerFlag,
        type: DataType.DropDown,
        dropDownList: FaultLogCustomerFlagList,
      },
      {
        label: Constants.Label_CustomerName,
        name: Constants.Field_CustomerName,
        type: DataType.Text,
      },
      {
        label: Constants.Label_CustomerPostCode,
        name: Constants.Field_CustomerPostCode,
        type: DataType.Text,
      },
      {
        label: Constants.Label_ShipTo,
        name: Constants.Field_ShipTo,
        type: DataType.Text,
      },
      {
        label: Constants.Label_DeviceID,
        name: Constants.Field_DeviceID,
        type: DataType.Text,
      },
      {
        label: Constants.Label_DeviceType,
        name: Constants.Field_DeviceType,
        type: DataType.Text,
      },
      {
        label: Constants.Label_FaultCode,
        name: Constants.Field_FaultCode,
        type: DataType.DropDown,
        dropDownList: faultCodesCodeList,
      },
      {
        label: Constants.Label_ID,
        name: Constants.Field_ID,
        type: DataType.Int,
      },
      {
        label: Constants.Label_ImpactOnLogistics,
        name: Constants.Field_ImpactOnLogistics,
        type: DataType.DropDown,
        dropDownList: FaultLogLogisticsImpactList,
      },
      {
        label: Constants.Label_Owner,
        name: Constants.Field_Owner,
        type: DataType.DropDown,
        dropDownList: FaultLogOwnerList,
      },
      {
        label: Constants.Label_Priority,
        name: Constants.Field_Priority,
        type: DataType.DropDown,
        dropDownList: FaultLogPriorityList,
      },
      {
        label: Constants.Label_Product,
        name: Constants.Field_Product,
        type: DataType.DropDown,
        dropDownList: products.map((p) => p.displayProductName),
      },
      {
        label: Constants.Label_Region,
        name: Constants.Field_Region,
        type: DataType.DropDown,
        dropDownList: regionOptions.map((r) => r.value),
      },
      {
        label: Constants.Label_ReportedBy,
        name: Constants.Field_ReportedBy,
        type: DataType.Text,
      },
      {
        label: Constants.Label_ReportedTime,
        name: Constants.Field_ReportedTime,
        type: DataType.Date,
      },
      {
        label: Constants.Label_RootCause,
        name: Constants.Field_RootCause,
        type: DataType.Text,
      },
      {
        label: Constants.Label_SapNotificationNumber,
        name: Constants.Field_SapNotificationNumber,
        type: DataType.Int,
      },
      {
        label: Constants.Label_SiteVisitDate,
        name: Constants.Field_SiteVisitDate,
        type: DataType.Date,
      },
      {
        label: Constants.Label_Status,
        name: Constants.Field_Status,
        type: DataType.DropDown,
        dropDownList: FaultLogStatusList,
      },
      {
        label: Constants.Label_TelemetrySystem,
        name: Constants.Field_TelemetrySystem,
        type: DataType.Text,
      },
      {
        label: Constants.Label_ToEngineeringDate,
        name: Constants.Field_ToEngineeringDate,
        type: DataType.Date,
      },
      {
        label: Constants.Label_VesselId,
        name: Constants.Field_VesselId,
        type: DataType.Text,
      },
      {
        label: Constants.Label_WorkOrderUserStatus,
        name: Constants.Field_WorkOrderUserStatus,
        type: DataType.Text,
      },
      {
        label: Constants.Label_NumberOfDeliveries,
        name: Constants.Field_NumberOfDeliveries,
        type: DataType.Int,
      },
    ],
    [countryOptions, faultCodesCodeList, products, regionOptions]
  );

  useEffect(() => {
    dashboardState.isInitialized && loading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardState.isInitialized]);

  const handleNewFaultLog = () => {
    showEditForm({} as FaultLog);
  };

  const handleExportToExcel = () => {
    const exportData = data.map((item) => ({
      ID: item.id,
      Acknowledged: item.acknowledged,
      "Acknowledged By": item.acknowledgedBy,
      "Acknowledged Time": DateTimeHelper.ToLocalString(item.acknowledgedTime),
      "Asset Name": item.assetName?.join(", "),
      "Closed Date": item.closedDate,
      Country: item.country,
      "Customer Flag": item.customerFlag?.join(", "),
      "Customer Name": item.customerName?.join(", "),
      "Customer City": item.customerCity?.join(", "),
      "Customer State Code": item.customerStateCode?.join(", "),
      "Customer Ship To": item.customerShipTo?.join(", "),
      "Customer Demand Type": item.customerDemandType?.join(", "),
      "Customer Post Code": item.customerPostCode?.join(", "),
      "Customer Terminal": item.terminal?.join(", "),
      "Customer Product": item.product?.join(", "),
      "# of Deliveries in Last 30 Days": item.numOfDeliveries?.join(", "),
      "Customer Vessel ID": item.vesselId?.join(", "),
      "Device ID": item.deviceId?.join(", "),
      "Device Type": item.deviceType?.join(", "),
      "Fault Code": item.faultCode,
      "Latest Comments": item.latestComments,
      Status: item.status,
      "Impact On Logistics": item.impactOnLogistics,
      Priority: item.priority,
      Owner: item.owner,
      Region: item.region,
      "Reported By": item.reportedBy,
      "Reported Time": DateTimeHelper.ToLocalString(item.reportedTime!),
      "Root Cause": item.rootCause,
      "SAP Notification Number": item.sapNotificationNumber,
      "Site Visit Date": item.siteVisitDate,
      "Telemetry System": item.telemetrySystem,
      "To Engineering Date": item.toEngineeringDate,
      "Work Order Number": item.workOrderNumber,
      "Work Order User Status": item.workOrderUserStatus,
      "Work Order Instructions": item.workOrderInstructions,
      "Created By": item.createdBy,
      "Created At": DateTimeHelper.ToLocalString(item.createdAt),
      "Last Modified By": item.lastModifiedBy,
      "Last Modified At": DateTimeHelper.ToLocalString(item.lastModifiedAt),
    }));

    ExcelHelper.ExportToExcel(exportData, "Fault Log Data", `FaultLog_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleResetFilterAndSearchClick = async () => {
    const profile = await UserProfileApi.getUserProfile(currentApexId);
    const defaultFilters = {
      isInitialized: true,
      filterRegionList: profile?.region || [],
      filterTerminalList: profile?.primaryTerminalIds || [],
      filterProductList: profile?.productClassIds || [],
    };
    store.dispatch(resetFilters(defaultFilters));
    await loading({
      ...dashboardState,
      ...defaultFilters,
      openAdvancedQuery: false,
      filterStartTime: "",
      filterEndTime: "",
      advancedFilters: [],
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("reset") === "true") {
      handleResetFilterAndSearchClick();
      // Remove the parameter from the URL without refreshing
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearchClick = async () => {
    await loading();
  };

  return (
    <>
      <CardContainer
        title="Dashboard"
        subTitle="Overview of fault records and telemetry data"
        itemCount={data.length}
        itemText={"Total Faults"}
      >
        <div>
          <div className="bg-light border-bottom p-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <Button
                  className="me-2 px-3 py-2"
                  size="sm"
                  variant={dashboardState.openFilter ? "outline-primary" : "primary"}
                  onClick={() => store.dispatch(setOpenFilter(!dashboardState.openFilter))}
                  aria-expanded={dashboardState.openFilter}
                >
                  {dashboardState.openFilter ? "Hide" : "Show"} Filter
                </Button>

                <Button
                  className="me-2 px-3 py-2"
                  size="sm"
                  variant={dashboardState.openAdvancedQuery ? "primary" : "outline-primary"}
                  onClick={() => store.dispatch(setOpenAdvancedQuery(!dashboardState.openAdvancedQuery))}
                  aria-expanded={dashboardState.openAdvancedQuery}
                >
                  {dashboardState.openAdvancedQuery ? "Use Basic Query" : "Use Advanced Query"}
                </Button>

                <Button className="me-2 px-3 py-2" size="sm" variant="outline-primary" onClick={handleResetFilterAndSearchClick}>
                  Reset Filter & Search
                </Button>

                <Button className="me-2 px-3 py-2" size="sm" variant="outline-primary" onClick={handleSearchClick}>
                  Search
                </Button>
              </div>

              <div>
                {!isReader(userRoles) && !isHandler(userRoles) && (
                  <Button className="me-2 px-3 py-2" size="sm" variant="outline-primary" onClick={handleNewFaultLog}>
                    New Fault
                  </Button>
                )}
                <Button className="px-3 py-2" size="sm" variant="outline-primary" onClick={handleExportToExcel}>
                  Export
                </Button>
              </div>
            </div>

            <div>
              <Collapse in={dashboardState.openFilter}>
                <div id="collapse-content" style={{ minHeight: dashboardState.openFilter ? "auto" : 0 }}>
                  <Collapse in={!dashboardState.openAdvancedQuery}>
                    <div
                      id="collapse-content"
                      className="mt-1"
                      style={{ minHeight: !dashboardState.openAdvancedQuery ? "auto" : 0 }}
                    >
                      <FaultLogFilter />
                    </div>
                  </Collapse>
                  <Collapse in={dashboardState.openAdvancedQuery}>
                    <div
                      id="collapse-content"
                      className="mt-1"
                      style={{ minHeight: dashboardState.openAdvancedQuery ? "auto" : 0 }}
                    >
                      <AdvancedFilter
                        defaultFilterList={dashboardState.advancedFilters}
                        fieldNameList={AdvancedFitlerfieldNameList}
                        onFiltersChange={(e) => store.dispatch(setAdvancedFilters(e))}
                      />
                    </div>
                  </Collapse>
                </div>
              </Collapse>
            </div>
          </div>
          {<MessageData messages={toastInfo}></MessageData>}
          {data.length >= 1000 && (
            <Alert variant="warning" className="mx-3 mt-1 mb-0">
              Too many results ({data.length.toLocaleString()} records). Only the first 1,000 records are shown. Please refine
              your search filters to narrow down the results.
            </Alert>
          )}
          <div className="p-3">
            <TanstackTable
              data={data}
              columns={columns}
              shouldBlinkRow={(row: FaultLog) => row.isFlashing === true}
              blinkColor={(row: FaultLog) => row.FlashingColor}
              showTopScrollBar={true}
            ></TanstackTable>
          </div>
        </div>
      </CardContainer>
    </>
  );
};

export default Dashboard;
